import React, { useState, useEffect, useMemo } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Form, Input } from "antd";

import { responsive } from "../../../utils/style";
import ConfirmModal from "../../../components/Nutrition/Modal";
import { updateStudentNutrition, getStudent } from "../../../stores/nutrition";
import { TOTAL_STEPS } from "./index";
import { parseDateToString } from "../../../utils/date";
import {
  FieldContainer,
  CustomSelect,
  Option,
  SubmitButton,
} from "../../../components/CustomFields";
import {
  HEIGHT_FOR_AGE_OPTIONS,
  WEIGHT_FOR_HEIGHT_OPTIONS,
} from "../../../utils/nutrition";

const validateMessages = {
  required: "",
};

const NutritionForm = () => {
  const dispatch = useDispatch();
  let navigate = useNavigate();
  const { step } = useParams();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("studentId");

  const [form] = Form.useForm();

  const [, forceUpdate] = useState({});
  const [isConfirm, setConfirm] = useState(false);

  const { student, rule } = useSelector((state) => state.nutrition);

  const now = new Date();
  const currentDate = parseDateToString(now, "th-TH", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  useEffect(() => {
    // To disable submit button at the beginning.
    forceUpdate({});

    dispatch(getStudent({ step, userId }));
  }, []);

  useEffect(() => {
    // To prevent invalid week
    const steps = TOTAL_STEPS.map((t) => t.step);

    if (!steps.includes(parseInt(step)) || now < rule[parseInt(step)]) {
      navigate("/");
    }
  }, [rule[parseInt(step)]]);

  const initialValues = useMemo(() => {
    return student;
  }, [student]);

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [initialValues]);

  const onFinish = () => setConfirm(true);
  const onCancel = () => setConfirm(false);
  const onSubmit = () => {
    dispatch(
      updateStudentNutrition({
        values: { ...student, ...form.getFieldsValue(true) },
        onSuccess: () => navigate(-1),
      })
    );
  };

  return (
    <Container>
      <Background>
        <ConfirmModal
          visible={isConfirm}
          onCancel={onCancel}
          onSubmit={onSubmit}
          event={step}
          formValues={form.getFieldsValue(true)}
        />
        <Topic>กรอกข้อมูลภาวะโภชนาการครั้งที่ {step}</Topic>
        <SubTopic>{`${student.name} ชั้น ม.${student.classNo} ห้อง ${student.roomNo}`}</SubTopic>
        <Form
          form={form}
          name="nutrition"
          layout="vertical"
          autoComplete="off"
          validateMessages={validateMessages}
          onFinish={onFinish}
        >
          <ShowDate>{currentDate}</ShowDate>
          <FieldContainer
            name="weight"
            label="น้ำหนัก (กิโลกรัม)"
            rules={[{ required: true }]}
            marginBottom={0}
          >
            <Input placeholder="โปรดระบุ" type="number" />
          </FieldContainer>
          <FieldContainer
            name="height"
            label="ส่วนสูง (เซนติเมตร)"
            rules={[{ required: true }]}
            marginBottom={0}
          >
            <Input placeholder="โปรดระบุ" type="number" />
          </FieldContainer>
          <FieldContainer
            name="heightForAge"
            label="ผลการประเมินส่วนสูง (กดเลือกตามผลการประเมินของท่าน)"
            marginBottom={0}
          >
            <CustomSelect
              placeholder="โปรดระบุ"
              allowClear
              width="100%"
              widthRs="100%"
            >
              {HEIGHT_FOR_AGE_OPTIONS.map((d, i) => (
                <Option key={i} value={d.value}>
                  {d.label}
                </Option>
              ))}
            </CustomSelect>
          </FieldContainer>
          <FieldContainer
            name="weightForHeight"
            label="ผลการประเมินน้ำหนักตัว (กดเลือกตามผลการประเมินของท่าน)"
            marginBottom={0}
          >
            <CustomSelect
              placeholder="โปรดระบุ"
              allowClear
              width="100%"
              widthRs="100%"
            >
              {WEIGHT_FOR_HEIGHT_OPTIONS.map((d, i) => (
                <Option key={i} value={d.value}>
                  {d.label}
                </Option>
              ))}
            </CustomSelect>
          </FieldContainer>
          <Form.Item shouldUpdate>
            {() => (
              <SubmitWrapper>
                <SubmitButton
                  htmlType="submit"
                  disabled={
                    !form.getFieldValue("weight") ||
                    !form.getFieldValue("height") ||
                    !!form
                      .getFieldsError()
                      .filter(({ errors }) => errors.length).length
                  }
                >
                  บันทึกข้อมูล
                </SubmitButton>
              </SubmitWrapper>
            )}
          </Form.Item>
        </Form>
      </Background>
    </Container>
  );
};

export default NutritionForm;

const Container = styled.div`
  font-family: Noto Sans Thai, Noto Sans;
  background-color: #fffbeb;

  input {
    border-radius: 4px;
    height: 52px;
    font-size: 18px;
    margin-bottom: 24px;

    ${responsive.mobile(css`
      margin-bottom: 16px;
    `)}
  }

  form {
    max-width: 440px;
  }
`;
const Background = styled.div`
  width: 1144px;
  background-color: white;
  padding: 40px 187px;
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${responsive.tablet(css`
    width: 100%;
    padding: 40px 0;
    margin: auto;
  `)}

  ${responsive.mobile(css`
    padding: 24px;
  `)}
`;
const Topic = styled.h5`
  font-style: normal;
  font-weight: bold;
  font-size: 28px;
  line-height: 34px;
  letter-spacing: -0.02em;
  color: #058459;
  text-align: center;
  margin-bottom: 46px;

  ${responsive.mobile(css`
    font-size: 18px;
    margin-bottom: 24px;
  `)}
`;
const SubmitWrapper = styled.div`
  text-align: center;
`;
const ShowDate = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 26px;
  color: #4b5768;
  align-self: flex-start;
  margin-bottom: 16px;

  ${responsive.mobile(css`
    font-size: 14px;
  `)}
`;
const SubTopic = styled.p`
  font-weight: 500;
  font-size: 21px;
  line-height: 23px;
  /* identical to box height, or 110% */

  text-align: center;
  letter-spacing: -0.02em;

  /* Primary/Green 400 */

  color: #16af7b;
`;
